import React, { useContext, useEffect, useState, useCallback,
                useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ShopperContext } from '../context/shopper';
import { SkusFound } from './SkusFound';
import * as Constants from '../constants';
import { useForm } from "../hooks/useForm";
import { useStyles } from "../hooks/useStyles";
import { ErrorList } from './ErrorList';
import { BackButton } from './BackButton';
import { HeadingBuilder } from './HeadingBuilder';
import styles from '../css/trTdFirstChild.module.css';
import { useTranslation } from "react-i18next";
import Table from 'react-bootstrap/Table';
import encodeurl from "encodeurl";
import MyScannerA from './MyScannerA';

// Empty search filters. Module-level so its identity is stable (used
// as the seed and as the "cleared" target for Reset Filters).
const INITIAL_SEARCH_STATE = {
  brand: '',
  label: '',
  identifier: '',
  store_label: '',
  detail: ''
};

export const SkuSearch = () => {
  const { signedIn, foundSkus, searchSkus, circles,
          barcodeScanningIsActive,  // a feature flag for the barcode scanner
          errorsDoExist, dismissErrorsList, setErrorsDoExistToFalse,
          savedSkuSearch, setSavedSkuSearch, setFoodFactInfo,
          setJustGotClearToFalse, unsetFoundSkus } = useContext(ShopperContext);

  const {circle_id} = useParams();
  console.log('SkuSearch.js circle_id: ', circle_id);
  const circle = circles.find((c) =>  c.id === circle_id ) || {};
  // TODO: Under what circumstances would we get '{}'? Is this needed?
  // Seems like trouble when we access something like: {circle.currency_symbol}

  const { tableShow, tableData } = useStyles();
  const { t } = useTranslation();

  // Seed from a saved snapshot so browser Back restores the prior
  // search view (filled filters + results). reset() is avoided (it
  // would restore the seed, not empty) — Reset Filters blanks the
  // fields via editFormValues below.
  const { formData, handleChange, setFieldValue, editFormValues } =
    useForm(savedSkuSearch?.formData || INITIAL_SEARCH_STATE);

  const [searchInProgress, setSearchInProgress] = useState(false);
  const [showSearchResults, setShowSearchResults] =
    useState(savedSkuSearch?.showResults ?? false);
  const [ showScanner, setShowScanner ] = useState(false);

  const handleOpenScanner = useCallback(() => {
    setShowScanner(true);
  }, []);
  
  const handleCloseScanner = useCallback(() => {
    setShowScanner(false);
  }, []);
  
  const resetFilters = useCallback(() => {
    editFormValues(INITIAL_SEARCH_STATE);  // blank the filters
    setSavedSkuSearch(null);
    setFoodFactInfo(null);  // drop the OFF panel tied to this search
  }, [editFormValues, setSavedSkuSearch, setFoodFactInfo]);

  const wants12digitEAN =  circle.symbology === 'upca' ? true : false;

  const tippySkuIdentifier =
    t('sku.identifier.tippyContent', {
      postscript:
    barcodeScanningIsActive
      ?
          t('sku.identifier.barScanTippyContentA')
        + t('sku.identifier.barScanTippyContentB', {
            barcodeAction: t('sku.identifier.replaces'),
          })
      : ''
    })
  
  // Hide stale results once the shopper edits a filter — but not on
  // the initial render, which would wipe a snapshot restored on Back.
  const skipFirstFilterEffect = useRef(true);
  useEffect(() => {
      if (skipFirstFilterEffect.current) {
        skipFirstFilterEffect.current = false;
        return;
      }
      setShowSearchResults(false);
  }, [formData]);
  
  useEffect(() => {
    if (errorsDoExist) {
      setSearchInProgress(false)
    }
  }, [errorsDoExist]);

  if (signedIn) {
    const handleSubmit = (e) => {
      e.preventDefault();
            
      // clear out any error messages, success statuses
      dismissErrorsList();
      setErrorsDoExistToFalse();
      setJustGotClearToFalse();
      setSearchInProgress(true);
  
      console.log('formData for editSku: ', formData);
      const formDataWithCircId = { ...formData, circ_id: circle_id }

      const query = Object.entries(formDataWithCircId)
        .filter(([key, value]) => value.length > 0)
        .map(([key, value]) => `${key}=${encodeurl(value)}`)
        .join("&");
      console.log('encodeurl of formdata: ', query);

      // Snapshot this search so browser Back from the Add Sku page
      // restores the filled-in filters and the results view.
      setSavedSkuSearch({ formData: { ...formData }, showResults: true });

      // Delay showing a count of foundSkus until 'searchSkus' is done.
      // Otherwise, a placeholding/wrong value appears right away (not helpful.)
      // In fact, use that time to show a "Searching..." message.
      (async function () {
        await searchSkus(query);
        setSearchInProgress(false);
        setShowSearchResults(true);
      })();
      console.log('skus found: ', foundSkus);

      // If there is an error we want to preserve formData and show the message.
    }

    const clearSearchResults = (e) => {
      unsetFoundSkus();
    }

    const skuSearchHeading =
        t('sku.searchOtherCirclesForSku.tippyContent')
      + t('sku.searchOtherCirclesForSku.tippyContentFormNote', {
          max_sku_search_results: Constants.MAX_SKU_SEARCH_RESULTS
        })

    return (
      <>
        <article>
          <hr/>
          <HeadingBuilder
            Size        = 'h1'
            headingText = {t('sku.searchOtherCirclesForSku.headingText')}
            value       = {circle.identifier}
            // Add Sku for Circle: uipuy
          />
          <HeadingBuilder
            Size           = 'h3'
            headingText    = {t('sku.formPart.skuSearchHeadlineLabel')}
            // Search for Skus matching:
            tippyAlertText = {skuSearchHeading}
            tippyText      = {skuSearchHeading}
          />
          <form onSubmit={handleSubmit}>
<Table responsive striped bordered hover size="sm" style={tableShow}>
  <thead >
  </thead>
  <tbody className={styles.trTdFC} style={tableData} >
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('sku.store_label.label')}
          // Store
          labelHtmlFor   = 'store_label_sks'
          tippyAlertText = {t('sku.store_label.tippyContentSearch')}
          tippyText      = {t('sku.store_label.tippyContentSearch')}
          // Every Sku has info about the Store where it is purchased...
        />
      </td>
      <td>
        <input
          key="10"
          type="text"
          id="store_label_sks"
          name="store_label"
          onChange={handleChange}
          value={formData.store_label}
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('sku.identifier.label')}
          // Lookup ID
          buttonAction   = {
                             barcodeScanningIsActive
                               ? handleOpenScanner
                               : null
                           }
          goButtonText   = {
                             barcodeScanningIsActive
                               ? Constants.GET_ICON_BARCODE
                               : ''
                           }
          // buttonAction   = {handleOpenScanner}
          // goButtonText   = {Constants.GET_ICON_BARCODE}
          labelHtmlFor   = "identifier_sks"
          tippyAlertText = {tippySkuIdentifier}
          // A value that identifies the item such as a store code, barcode, or a term you make up. Include only uppercase letters, numbers, periods, underscores ('_'), and nonconsecutive spaces.
          tippyText      = {Constants.DOT}
        />
      </td>
      <td>
        {showScanner
          ? <
            MyScannerA
              inputFieldName     = 'identifier'
              handleFieldValue   = {setFieldValue}  // replaces input w bcode
              handleCloseScanner = {handleCloseScanner}
              wants12digitEAN    = {wants12digitEAN}
            />
          :
        <input
          key="20"
          type="text"
          id="identifier_sks"
          name="identifier"
          onChange={handleChange}
          value={formData.identifier}
        />
        }
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('sku.brand.label')}
          // Brand
          labelHtmlFor   = 'brand_sks'
          tippyAlertText = {t('sku.brand.tippyContent')}
          tippyText      = {t('sku.brand.tippyContent')}
          // e.g. Green Giant, General Mills, Heinz.
        />
      </td>
      <td>
        <input
          key="30"
          type="text"
          id="brand_sks"
          name="brand"
          onChange={handleChange}
          value={formData.brand}
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('sku.label.label')}
          // Name/Label
          labelHtmlFor   = 'label_sks'
          tippyAlertText = {t('sku.label.tippyContentSearch')}
          tippyText      = {t('sku.label.tippyContentSearch')}
          // Short, quick name of the item
        />
      </td>
      <td>
        <input
          key="40"
          type="text"
          id="label_sks"
          name="label"
          onChange={handleChange}
          value={formData.label}
        />
      </td>
    </tr>
    <tr>
      <td>
        <HeadingBuilder
          containerType  = 'container-input-label'
          headingText    = {t('sku.detail.label')}
          // Details
          labelHtmlFor   = 'detail_sks'
          tippyAlertText = {t('sku.detail.tippyContentSearch')}
          tippyText      = {t('sku.detail.tippyContentSearch')}
          // Additional details/info about the item
        />
      </td>
      <td>
        <textarea
          key="50"
          id="detail_sks"
          name="detail"
          onChange={handleChange}
          value={formData.detail}
        />
      </td>
    </tr>
  </tbody>
</Table>
          {/* TODO: ?Will translating Submit be handled automatically by html?
              https://www.w3.org/International/wiki/Locale-based_forms */}
            <input key="170" type='submit' value='Submit' />
          </form>
          { errorsDoExist && <ErrorList /> }

          <Table responsive>
            <thead>
            </thead>
            <tbody>
              <tr>
                <td>
                  {/* Button to reset search fields. Only appears if any search text. */}
                  { [
                      formData.brand,
                      formData.label,
                      formData.identifier,
                      formData.store_label,
                      formData.detail
                    ].some( (element) => element.length > 0 )
        
                    ? <HeadingBuilder
                        containerType  = 'oneline-container-left'
                        headingText    = {t('sku.reset.buttonText')}
                        // Reset Filters
                        buttonAction   = {resetFilters}
                        tippyAlertText = {t('sku.reset.tippyContent', {
                          searchResultsComment:
                            foundSkus.length > 0
                              ? t('sku.reset.searchResultsComment')
                              : ''
                        })}
                        tippyText      = {Constants.DOT}
                        // Click this button to blank out the Search fields.
                        // This will not clear any Search Results.
                      />
                    : foundSkus.length > 0
                      ? <HeadingBuilder
                          containerType  = 'oneline-container-left'
                          headingText    ={t('sku.reset.clearSearchButtonText')}
                          // Clear Search Results
                          buttonAction   = {clearSearchResults}
                        tippyAlertText ={t('sku.reset.clearSearchTippyContent')}
                          tippyText      = {Constants.DOT}
                          // Click this button to clear out any Search Results.
                        />
                      : null
                  }
                </td>
                <td>
                  { searchInProgress
                     ? <HeadingBuilder
                         headingText    = {t('global.searching')}
                         // Searching...
                       />
                     : showSearchResults
                       ? <HeadingBuilder
                           headingText    = {t('sku.searchOtherCirclesForSku.matchCount')}
                           valueNoEm      = {foundSkus.length.toString()}
                           // Skus found: 14
                           tippyAlertText = {t('sku.searchOtherCirclesForSku.tippyContentMatchCount')}
                           tippyText      = {t('sku.searchOtherCirclesForSku.tippyContentMatchCount')}
                         />
                       : null
                  } 
                </td>
                <td>
                  <BackButton lockRight={false}/>
                  {/* <BackButton whatToDo={resetSkuTypeFormFlags}/> */}
                </td>
              </tr>
            </tbody>
          </Table>
        </article>
        <article>
          { foundSkus
            ? <SkusFound
                circle_id={circle_id} 
                currency_symbol={circle.currency_symbol}
                cstu={circle.currency_subunit}
                otherSkus={foundSkus}
                showSearchResults={showSearchResults}
                wants12digitEAN={wants12digitEAN}
                identifier={formData.identifier}
              />
            : <h3>{t('sku.noSkus')}</h3>
              // No Skus
          }
        </article>
      </>
      )
    } else {
      return (
        <HeadingBuilder
          Size = 'h3'
          headingText = {t('global.notSignedInMessage')}
          // Not Authorized - Please Sign in or Sign up
        />
      )
    }
}

export default SkuSearch